/**
 * LE SUIVI QUI DEVIENT UNE ACTION (demande du fondateur le 23/10/2026) :
 * choisir « Rappel de vaccin » propose trois delais (« dans un mois et demi »)
 * et un calendrier pour toute autre date ; un clic cree la tache datee du
 * dossier, assignee au veterinaire. Logique sans ecran, testee par
 * `suivi.test.mjs`.
 *
 * Les cles sont les VALEURS du champ `followUp` (`taki.fields.json`, libelle
 * comme valeur). Une valeur inconnue (« Aucun » des anciennes consultations)
 * n'a pas de suite.
 */

export interface Delai {
  libelle: string;
  jours?: number;
  mois?: number;
}

export interface SuiteDuSuivi {
  /** Ce que dit la tache, avant le nom de l'animal. */
  tache: string;
  delais: readonly Delai[];
  /** Le delai choisi d'office (son libelle). */
  parDefaut: string;
  /** Vrai : la date se pose aussi en rappel sur les vaccins de la consultation. */
  rappelDesVaccins?: boolean;
  /** Vrai : le libelle de la tache s'ecrit a la main (« Autre »). */
  libre?: boolean;
}

export const SUITES_DU_SUIVI: Readonly<Record<string, SuiteDuSuivi>> = {
  /* Un seul controle, dont on choisit le delai (fondateur, 23/10 : « contrôle dans une semaine » n'avait pas de sens). */
  'Contrôle suivant': {
    tache: 'Contrôle',
    delais: [
      { libelle: '1 semaine', jours: 7 },
      { libelle: '2 semaines', jours: 14 },
      { libelle: '1 mois', mois: 1 },
    ],
    parDefaut: '1 semaine',
  },
  'Rappel de vaccin': {
    tache: 'Rappel de vaccin',
    delais: [
      { libelle: '3 semaines', jours: 21 },
      { libelle: '1 mois et demi', mois: 1, jours: 15 },
      { libelle: '1 an', mois: 12 },
    ],
    parDefaut: '1 an',
    rappelDesVaccins: true,
  },
  'Résultats à communiquer': {
    tache: 'Communiquer les résultats',
    delais: [
      { libelle: 'Demain', jours: 1 },
      { libelle: '2 jours', jours: 2 },
      { libelle: '1 semaine', jours: 7 },
    ],
    parDefaut: '2 jours',
  },
  Hospitalisation: {
    tache: "Préparer l'hospitalisation",
    delais: [
      { libelle: "Aujourd'hui", jours: 0 },
      { libelle: 'Demain', jours: 1 },
      { libelle: 'Après-demain', jours: 2 },
    ],
    parDefaut: "Aujourd'hui",
  },
  /* LA TACHE LIBRE (fondateur, 26/10) : « Autre » remplace « Aucun », on ecrit ce qu'il y a a faire. */
  Autre: {
    tache: 'Tâche',
    delais: [
      { libelle: 'Demain', jours: 1 },
      { libelle: '1 semaine', jours: 7 },
      { libelle: '1 mois', mois: 1 },
    ],
    parDefaut: '1 semaine',
    libre: true,
  },
};

/** La suite d'une valeur de suivi, ou null (« Aucun », vide, valeur inconnue). */
export function suiteDe(suivi: unknown): SuiteDuSuivi | null {
  return typeof suivi === 'string' ? (SUITES_DU_SUIVI[suivi] ?? null) : null;
}

/**
 * LA DATE DU DELAI, comptee depuis le jour de la consultation, a 9 h. Les mois
 * d'abord, puis les jours : « 1 mois et demi » apres le 31 janvier donne le
 * 15 mars (fin fevrier, plus quinze jours), jamais un 3 mars.
 */
export function dateDuDelai(depuis: Date, delai: Delai): Date {
  const date = new Date(depuis.getFullYear(), depuis.getMonth(), depuis.getDate(), 9, 0, 0, 0);
  if (delai.mois !== undefined && delai.mois !== 0) {
    const jour = date.getDate();
    date.setDate(1);
    date.setMonth(date.getMonth() + delai.mois);
    const dernier = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    date.setDate(Math.min(jour, dernier));
  }
  if (delai.jours !== undefined) date.setDate(date.getDate() + delai.jours);
  return date;
}

/** Le libelle de la tache : « Rappel de vaccin : Rex ». */
export function libelleDeLaTache(suite: SuiteDuSuivi, animal: string): string {
  return animal.trim() === '' ? suite.tache : `${suite.tache} : ${animal.trim()}`;
}

/** Une date choisie au calendrier (`YYYY-MM-DD`), a 9 h ; null si elle est vide ou fausse. */
export function dateDuCalendrier(saisie: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(saisie)) return null;
  const date = new Date(Number(saisie.slice(0, 4)), Number(saisie.slice(5, 7)) - 1, Number(saisie.slice(8, 10)), 9, 0, 0, 0);
  return Number.isNaN(date.getTime()) || date.getDate() !== Number(saisie.slice(8, 10)) ? null : date;
}

/**
 * L'ECHEANCE D'UNE TACHE EN MOTS, comptee en jours de calendrier :
 * « aujourd'hui », « demain », « dans 5 jours », « en retard de 2 jours ».
 * `retard` vaut vrai des la veille passee (une tache faite n'est jamais en retard).
 */
export function echeanceEnMots(echeance: Date, aujourdhui: Date, faite: boolean): { texte: string; retard: boolean } {
  const jour = (d: Date): number => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86_400_000;
  const ecart = jour(echeance) - jour(aujourdhui);
  if (ecart === 0) return { texte: "aujourd'hui", retard: false };
  if (ecart === 1) return { texte: 'demain', retard: false };
  if (ecart > 1) return { texte: `dans ${ecart} jours`, retard: false };
  if (faite) return { texte: ecart === -1 ? 'hier' : `il y a ${-ecart} jours`, retard: false };
  return { texte: ecart === -1 ? 'en retard depuis hier' : `en retard de ${-ecart} jours`, retard: true };
}
